import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { authClient } from "@/lib/auth";
import { AuthModal } from "@/components/auth/AuthModal";

type AcceptState = "loading" | "need-auth" | "accepting" | "success" | "error";

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [state, setState] = useState<AcceptState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const invitationId = searchParams.get("id");

  useEffect(() => {
    if (!invitationId) {
      setState("error");
      setError("Invalid invitation link — no invitation ID found.");
      return;
    }

    if (authLoading) return;

    if (!isAuthenticated) {
      setState("need-auth");
      return;
    }

    // User is authenticated — try to accept
    setState("accepting");
    acceptInvitation(invitationId);
  }, [invitationId, isAuthenticated, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const acceptInvitation = async (id: string) => {
    try {
      const result = await authClient.organization.acceptInvitation({
        invitationId: id,
      });
      if (result.error) {
        throw new Error(result.error.message || "Failed to accept invitation");
      }
      setState("success");
      // Redirect to settings after a brief pause
      setTimeout(() => navigate("/settings"), 2000);
    } catch (err) {
      setState("error");
      setError(err instanceof Error ? err.message : "Failed to accept invitation");
    }
  };

  if (state === "loading" || authLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
          <p className="text-sm text-slate-500">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (state === "need-auth") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-slate-900">Sign in to accept</h2>
          <p className="mb-6 text-sm text-slate-500">
            You need to sign in or create an account to accept this invitation.
          </p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="rounded-xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Sign in or Sign up
          </button>
          <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
        </div>
      </div>
    );
  }

  if (state === "accepting") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
          <p className="text-sm text-slate-500">Accepting invitation...</p>
        </div>
      </div>
    );
  }

  if (state === "success") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 13l4 4L19 7"
                stroke="#16a34a"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-xl font-bold text-slate-900">Invitation accepted</h2>
          <p className="text-sm text-slate-500">Redirecting to settings...</p>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="#dc2626"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <h2 className="mb-2 text-xl font-bold text-slate-900">Something went wrong</h2>
        <p className="mb-4 text-sm text-slate-500">{error}</p>
        <button
          onClick={() => navigate("/")}
          className="rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
        >
          Go home
        </button>
      </div>
    </div>
  );
}
